import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { ConfirmationDialog } from '../Common/ConfirmationDialog';

// SVG Icons
const MoreIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
    <circle cx="8" cy="3" r="1.5" />
    <circle cx="8" cy="8" r="1.5" />
    <circle cx="8" cy="13" r="1.5" />
  </svg>
);

const DuplicateIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3,6 5,6 21,6" />
    <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2v2" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9,6 15,12 9,18" />
  </svg>
);

interface ContainerActionsMenuProps {
  containerId: string;
  containerName: string;
  /** Whether the container's row has space for another container (max 3) */
  canDuplicateInSameRow: boolean;
  onDelete: (containerId: string) => void;
  /** Duplicate container in the same row (requires canDuplicateInSameRow=true) */
  onDuplicateInSameRow: (containerId: string) => void;
  /** Duplicate container in a new row below */
  onDuplicateInNewRow: (containerId: string) => void;
}

export const ContainerActionsMenu = ({
  containerId,
  containerName,
  canDuplicateInSameRow,
  onDelete,
  onDuplicateInSameRow,
  onDuplicateInNewRow,
}: ContainerActionsMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDuplicateSubmenu, setShowDuplicateSubmenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [submenuPosition, setSubmenuPosition] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);
  const duplicateItemRef = useRef<HTMLDivElement>(null);
  const submenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openSubmenu = useCallback(() => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
      submenuTimeoutRef.current = null;
    }
    setShowDuplicateSubmenu(true);
  }, []);

  const closeSubmenuWithDelay = useCallback(() => {
    submenuTimeoutRef.current = setTimeout(() => {
      setShowDuplicateSubmenu(false);
      submenuTimeoutRef.current = null;
    }, 150);
  }, []);

  // Calculate dropdown position when menu opens
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.right - 140, // 140 = min-w-[140px], right-aligned to trigger
      });
    } else {
      setMenuPosition(null);
      setSubmenuPosition(null);
    }
  }, [isOpen]);

  // Calculate submenu position when it opens
  useEffect(() => {
    if (showDuplicateSubmenu && duplicateItemRef.current) {
      const rect = duplicateItemRef.current.getBoundingClientRect();
      setSubmenuPosition({
        top: rect.top,
        left: rect.right + 4,
      });
    } else {
      setSubmenuPosition(null);
    }
  }, [showDuplicateSubmenu]);

  // Close menu when clicking outside (portal-aware)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideMenu =
        menuRef.current?.contains(target) ||
        dropdownRef.current?.contains(target) ||
        submenuRef.current?.contains(target);
      if (!isInsideMenu) {
        setIsOpen(false);
        setShowDuplicateSubmenu(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close menu on scroll of nearest scrollable ancestor
  const closeMenu = useCallback(() => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
      submenuTimeoutRef.current = null;
    }
    setIsOpen(false);
    setShowDuplicateSubmenu(false);
  }, []);

  useEffect(() => {
    return () => {
      if (submenuTimeoutRef.current) clearTimeout(submenuTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const scrollParent = menuRef.current?.closest('.tw-overflow-auto');
    if (scrollParent) {
      scrollParent.addEventListener('scroll', closeMenu);
      return () => scrollParent.removeEventListener('scroll', closeMenu);
    }
  }, [isOpen, closeMenu]);

  const handleDuplicateInSameRow = () => {
    onDuplicateInSameRow(containerId);
    setIsOpen(false);
    setShowDuplicateSubmenu(false);
  };

  const handleDuplicateInNewRow = () => {
    onDuplicateInNewRow(containerId);
    setIsOpen(false);
    setShowDuplicateSubmenu(false);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
    setIsOpen(false);
    setShowDuplicateSubmenu(false);
  };

  const handleDeleteConfirm = () => {
    onDelete(containerId);
    setShowDeleteConfirm(false);
  };

  return (
    <div ref={menuRef} className="tw-relative">
      <button
        ref={triggerRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
          setShowDuplicateSubmenu(false);
        }}
        className="tw-p-0.5 tw-rounded hover:tw-bg-surface-hover tw-text-ink-secondary hover:tw-text-ink tw-transition-colors"
        aria-label="Container actions"
        data-testid={`container-actions-${containerId}`}
      >
        <MoreIcon />
      </button>

      {/* Dropdown rendered via portal to escape overflow:hidden ancestors */}
      {isOpen && menuPosition && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="tw-fixed tw-bg-surface-raised tw-rounded tw-shadow-ds-md tw-border tw-border-edge tw-py-1 tw-z-[9999] tw-min-w-[140px]"
          style={{ top: menuPosition.top, left: menuPosition.left }}
        >
          {/* Duplicate menu item with submenu */}
          <div
            ref={duplicateItemRef}
            onMouseEnter={openSubmenu}
            onMouseLeave={closeSubmenuWithDelay}
          >
            <button
              className="tw-w-full tw-px-2.5 tw-py-1.5 tw-text-left tw-text-xs tw-text-ink hover:tw-bg-surface-hover tw-flex tw-items-center tw-justify-between"
              data-testid={`container-duplicate-${containerId}`}
            >
              <span className="tw-flex tw-items-center tw-gap-2">
                <DuplicateIcon />
                Duplicate
              </span>
              <ChevronRightIcon />
            </button>
          </div>

          <div className="tw-border-t tw-border-edge tw-my-0.5" />
          <button
            onClick={handleDeleteClick}
            className="tw-w-full tw-px-2.5 tw-py-1.5 tw-text-left tw-text-xs tw-text-tone-danger-ink hover:tw-bg-tone-danger-tint tw-flex tw-items-center tw-gap-2"
            data-testid={`container-delete-${containerId}`}
          >
            <DeleteIcon />
            Delete
          </button>
        </div>,
        document.body
      )}

      {/* Duplicate submenu rendered via separate portal */}
      {isOpen && showDuplicateSubmenu && submenuPosition && ReactDOM.createPortal(
        <div
          ref={submenuRef}
          className="tw-fixed tw-bg-surface-raised tw-rounded tw-shadow-ds-md tw-border tw-border-edge tw-py-1 tw-z-[9999] tw-min-w-[120px]"
          style={{ top: submenuPosition.top, left: submenuPosition.left }}
          onMouseEnter={openSubmenu}
          onMouseLeave={closeSubmenuWithDelay}
        >
          <button
            onClick={handleDuplicateInSameRow}
            disabled={!canDuplicateInSameRow}
            className={`tw-w-full tw-px-2.5 tw-py-1.5 tw-text-left tw-text-xs tw-flex tw-items-center tw-gap-2 ${
              canDuplicateInSameRow
                ? 'tw-text-ink hover:tw-bg-surface-hover'
                : 'tw-text-ink-disabled tw-cursor-not-allowed'
            }`}
            title={!canDuplicateInSameRow ? 'Row is full (max 3 containers)' : 'Add duplicate to the same row'}
            data-testid={`container-duplicate-same-row-${containerId}`}
          >
            In Same Row
          </button>
          <button
            onClick={handleDuplicateInNewRow}
            className="tw-w-full tw-px-2.5 tw-py-1.5 tw-text-left tw-text-xs tw-text-ink hover:tw-bg-surface-hover tw-flex tw-items-center tw-gap-2"
            title="Create a new row below with the duplicate"
            data-testid={`container-duplicate-new-row-${containerId}`}
          >
            In New Row
          </button>
        </div>,
        document.body
      )}

      <ConfirmationDialog
        id={`delete-container-${containerId}`}
        isOpen={showDeleteConfirm}
        title="Delete Container"
        message={`Are you sure you want to delete "${containerName}"?`}
        secondaryMessage="This will also delete all nested containers and items. This action can be undone with Ctrl+Z."
        confirmText="Delete"
        confirmButtonClass="tw-bg-tone-danger-bold hover:tw-bg-tone-danger-bold-hover tw-text-ink-inverse"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};

export default ContainerActionsMenu;
